import React, { useState, useMemo, useRef, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  InputAdornment,
  IconButton,
  Button,
  Stack,
  Tooltip,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TablePagination,
} from "@mui/material";
import {
  Search as SearchIcon,
  Print as PrintIcon,
  Clear as ClearIcon,
  Receipt as ReceiptIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  FileDownload as FileDownloadIcon,
} from "@mui/icons-material";
import { useApiQuery, useApiMutation } from "../../hooks/useApi";
import { useReactToPrint } from "react-to-print";
import * as XLSX from "xlsx";
import api from "../../utils/api";

const mockBanks = [
  "Union Bank Fiscale (Advisor)",
  "Union Bank Fiscale (Solutions)",
  "Canara Fiscale (Advisor II)",
  "Cash (Advisor)",
  "Cash (Solutions)",
  "Cash (Advisor II)",
  "Other",
];

// Printable Receipt Component
const PrintableReceipt = React.forwardRef(({ receipt, client }, ref) => (
  <div
    ref={ref}
    style={{
      padding: "40px",
      width: "420px",
      margin: "0 auto",
      fontFamily: "monospace",
    }}
  >
    <style>{`
      @media print {
        @page {
          size: A5;
          margin: 10mm;
        }
      }
    `}</style>

    <h2 style={{textAlign: "center"}}>Fiscale</h2>
    <p style={{fontSize:"12px",textAlign: "center", marginBottom: "20px" }}>Koach Gaon, Biswanath Chariali, Assam, 784176, +91 6901945275</p>

    <p><strong>Receipt No:</strong> {receipt?.receiptNumber}</p>
    <p><strong>Date:</strong> {new Date(receipt?.collectedDate || Date.now()).toLocaleDateString()}</p>
    <p><strong>Amount:</strong> ₹{receipt?.amount}</p>
    <p><strong>Client Name:</strong> {client?.name}</p>
    <p><strong>Phone Number:</strong> {client?.phoneNumber}</p>
    <p><strong>Mode of Payment:</strong> {receipt?.paymentMethod}</p>
    {receipt?.transactionReference && (
      <p><strong>Transaction Ref:</strong> {receipt.transactionReference}</p>
    )}
    <p><strong>Collected By:</strong> {receipt?.collectedBy?.name || "—"}</p>

    <p style={{fontSize:"10px"}}>Received with thanks an amount of Rs. {receipt?.amount}/- (One Thousand Five Hundred only) from {client?.name}. Cheques are subject to realisation.</p>

    <hr style={{ margin: "20px 0" }} />

    <p style={{ textAlign: "center", opacity: 0.7 }}>Thank you for choosing Fiscale</p>
  </div>
));

function AdminAllReceiptsPage() {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(60);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [printDialogOpen, setPrintDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editData, setEditData] = useState({
    amount: "",
    paymentMethod: "",
    bank: "",
    transactionReference: "",
    notes: "",
    editComment: "",
  });
  const [editError, setEditError] = useState("");
  const [isExporting, setIsExporting] = useState(false);
  const receiptRef = useRef();

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(0);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const queryParams = useMemo(() => {
    const params = new URLSearchParams();
    params.set("page", String(page + 1));
    params.set("limit", String(rowsPerPage));
    if (debouncedSearch) {
      params.set("search", debouncedSearch);
    }
    return params.toString();
  }, [page, rowsPerPage, debouncedSearch]);

  const { data: response, isLoading, error, refetch } = useApiQuery(
    ["receipts", queryParams],
    `/receipts?${queryParams}`
  );

  const receiptsList = response?.receipts || [];
  const totalReceipts = response?.total || 0;

  const updateReceiptMutation = useApiMutation(`/receipts/${selectedReceipt?._id}`,
    "PUT",
    {
      onSuccess: () => {
        setEditDialogOpen(false);
        setSelectedReceipt(null);
        setEditData({
          amount: "",
          paymentMethod: "",
          bank: "",
          transactionReference: "",
          notes: "",
          editComment: "",
        });
        setEditError("");
        refetch();
      },
      onError: (error) => {
        setEditError(error.response.data.message || "Failed to update receipt");
      },
    }
  );

  const handlePrint = useReactToPrint({
    contentRef: receiptRef,
    documentTitle: `Receipt_${selectedReceipt?.receiptNumber || "Unknown"}`,
    onAfterPrint: () => {
      setPrintDialogOpen(false);
      setSelectedReceipt(null);
    },
  });

  const openPrintDialog = (receipt) => {
    setSelectedReceipt(receipt);
    setPrintDialogOpen(true);
  };

  const openEditDialog = (receipt) => {
    setSelectedReceipt(receipt);
    setEditData({
      amount: receipt.amount.toString(),
      paymentMethod: receipt.paymentMethod,
      bank: receipt.bank || "",
      transactionReference: receipt.transactionReference || "",
      notes: receipt.notes || "",
      editComment: "",
    });
    setEditError("");
    setEditDialogOpen(true);
  };

  const handleEditSubmit = () => {
    // Validate edit comment
    if (!editData.editComment.trim()) {
      setEditError("Edit comment is mandatory");
      return;
    }

    // Validate amount
    const amount = parseFloat(editData.amount);
    if (isNaN(amount) || amount < 0) {
      setEditError("Please enter a valid amount");
      return;
    }

    // Validate bank for bank_transfer
    if (editData.paymentMethod === "bank_transfer" && !editData.bank) {
      setEditError("Bank is required for bank transfers");
      return;
    }

    const oldAmount = selectedReceipt.amount;
    const newAmount = amount;

    // Prepare update data
    const updateData = {
      id: selectedReceipt._id,
      amount: newAmount,
      paymentMethod: editData.paymentMethod,
      bank: editData.paymentMethod === "bank_transfer" ? editData.bank : undefined,
      transactionReference: editData.transactionReference || undefined,
      notes: editData.notes || undefined,
      editComment: editData.editComment,
      oldAmount, // Send old amount to backend for balance adjustment
      clientId: selectedReceipt.clientId._id, // Send client ID for balance update
    };

    updateReceiptMutation.mutate(updateData);
  };

  const filteredReceipts = receiptsList;

  const getPaymentMethodColor = (method) => {
    const methodColors = {
      cash: "primary",
      bank_transfer: "success",
      cheque: "info",
      upi: "secondary",
      card: "warning",
    };
    return methodColors[method?.toLowerCase()] || "default";
  };

  const getClientName = (receipt) => {
    if (typeof receipt.clientId === "object" && receipt.clientId?.name) {
      return receipt.clientId.name;
    }
    return receipt.clientName || "Unknown Client";
  };

  const handleClearSearch = () => {
    setSearchTerm("");
  };

  const handleExportReceiptsToExcel = async () => {
    try {
      setIsExporting(true);
      const url = debouncedSearch
        ? `/receipts?export=true&search=${encodeURIComponent(debouncedSearch)}`
        : `/receipts?export=true`;

      const res = await api.get(url);
      const receiptsToExport = Array.isArray(res.data) ? res.data : (res.data?.receipts || []);

      if (!receiptsToExport || receiptsToExport.length === 0) {
        alert("No receipts found to export.");
        return;
      }

      const excelRows = receiptsToExport.map((receipt) => {
        const clientName = typeof receipt.clientId === "object" && receipt.clientId?.name
          ? receipt.clientId.name
          : receipt.clientName || "Unknown Client";

        const editHistoryFormatted = Array.isArray(receipt.editHistory) && receipt.editHistory.length > 0
          ? receipt.editHistory
              .map((edit, idx) => {
                const dateStr = edit.editedAt ? new Date(edit.editedAt).toLocaleString("en-IN") : "";
                const comment = edit.comment || edit.editComment || "";
                return `[Edit ${idx + 1} on ${dateStr}]: ${comment}`;
              })
              .join("\n")
          : "No edit history";

        return {
          "Receipt No.": receipt.receiptNumber || receipt.receiptId || "N/A",
          "Client": clientName,
          "Amount": receipt.amount ?? 0,
          "Payment Method": (receipt.paymentMethod || "N/A").toUpperCase(),
          "Bank": receipt.bank || "N/A",
          "Date": receipt.collectedDate ? new Date(receipt.collectedDate).toLocaleDateString("en-IN") : "N/A",
          "Edit History": editHistoryFormatted,
        };
      });

      const worksheet = XLSX.utils.json_to_sheet(excelRows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Fee Receipts");

      const todayStr = new Date().toISOString().split("T")[0];
      XLSX.writeFile(workbook, `Fiscale_Fee_Receipts_${todayStr}.xlsx`);
    } catch (err) {
      console.error("Export error:", err);
      alert("Failed to export receipts.");
    } finally {
      setIsExporting(false);
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Error loading receipts: {error.message}
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            All Fee Receipts (Admin)
          </Typography>
          <Typography variant="body1" color="textSecondary">
            Search, edit, and manage all fee receipts
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <IconButton onClick={() => refetch()} color="primary">
            <RefreshIcon />
          </IconButton>
          <Button
            variant="outlined"
            color="primary"
            startIcon={isExporting ? <CircularProgress size={20} /> : <FileDownloadIcon />}
            onClick={handleExportReceiptsToExcel}
            disabled={isExporting || receiptsList.length === 0}
          >
            {isExporting ? "Exporting..." : "Export to Excel"}
          </Button>
        </Stack>
      </Box>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <TextField
            fullWidth
            placeholder="Search by Receipt Number, Receipt ID, Client Name, Phone, Amount, Payment Method, or Bank..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton onClick={handleClearSearch} size="small">
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Stack>

        <Box sx={{ mt: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="body2" color="textSecondary">
            {totalReceipts} receipt{totalReceipts !== 1 ? "s" : ""} found
            {debouncedSearch && ` for "${debouncedSearch}"`}
          </Typography>
          {searchTerm && (
            <Button size="small" onClick={handleClearSearch}>
              Clear Search
            </Button>
          )}
        </Box>
      </Paper>

      <Paper sx={{ p: 3 }}>
        {filteredReceipts.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 6 }}>
            <ReceiptIcon sx={{ fontSize: 80, color: "text.secondary", mb: 2 }} />
            <Typography variant="h6" color="textSecondary" gutterBottom>
              {debouncedSearch ? "No receipts found" : "No receipts available"}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {debouncedSearch
                ? "Try adjusting your search criteria"
                : "Receipts will appear here once they are created"}
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Receipt No.</TableCell>
                  <TableCell>Client</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Payment Method</TableCell>
                  <TableCell>Bank</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Edit History</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredReceipts.map((receipt) => (
                  <TableRow key={receipt._id || receipt.id} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: "monospace", fontWeight: 600 }}>
                        {receipt.receiptNumber}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {getClientName(receipt)}
                      </Typography>
                      {receipt.clientId?.phoneNumber && (
                        <Typography variant="caption" color="textSecondary">
                          {receipt.clientId.phoneNumber}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold" color="success.main">
                        ₹{(receipt.amount || 0).toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={receipt.paymentMethod?.toUpperCase()}
                        color={getPaymentMethodColor(receipt.paymentMethod)}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      {receipt.bank ? (
                        <Chip label={receipt.bank} size="small" variant="outlined" />
                      ) : (
                        <Typography variant="body2" color="textSecondary">
                          —
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {new Date(receipt.collectedDate).toLocaleDateString()}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {new Date(receipt.collectedDate).toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {receipt.editHistory && receipt.editHistory.length > 0 ? (
                        <Tooltip
                          title={
                            <Box>
                              {receipt.editHistory.map((edit, idx) => (
                                <Box key={idx} sx={{ mb: 1 }}>
                                  <Typography variant="caption" display="block">
                                    <strong>Edit {idx + 1}:</strong>
                                  </Typography>
                                  <Typography variant="caption" display="block">
                                    {edit.comment}
                                  </Typography>
                                  <Typography variant="caption" display="block" color="textSecondary">
                                    {new Date(edit.editedAt).toLocaleString()}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          }
                        >
                          <Chip
                            label={`${receipt.editHistory.length} edit${receipt.editHistory.length > 1 ? "s" : ""}`}
                            size="small"
                            color="warning"
                          />
                        </Tooltip>
                      ) : (
                        <Typography variant="body2" color="textSecondary">
                          —
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={1}>
                        <Tooltip title="Edit Receipt">
                          <IconButton
                            color="primary"
                            size="small"
                            onClick={() => openEditDialog(receipt)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Print Receipt">
                          <IconButton
                            color="secondary"
                            size="small"
                            onClick={() => openPrintDialog(receipt)}
                          >
                            <PrintIcon />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        {filteredReceipts.length > 0 && (
          <TablePagination
            rowsPerPageOptions={[60, 100, 200]}
            component="div"
            count={totalReceipts}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        )}
      </Paper>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Typography variant="h6">Edit Receipt</Typography>
          <Typography variant="body2" color="textSecondary">
            Receipt No: {selectedReceipt?.receiptNumber}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            {editError && (
              <Alert severity="error" onClose={() => setEditError("")}>
                {editError}
              </Alert>
            )}

            <TextField
              fullWidth
              label="Amount"
              type="number"
              value={editData.amount}
              onChange={(e) => setEditData({ ...editData, amount: e.target.value })}
              error={parseFloat(editData.amount) < 0}
              helperText="Enter the receipt amount"
            />

            {/* <FormControl fullWidth>
              <InputLabel>Payment Method</InputLabel>
              <Select
                value={editData.paymentMethod}
                label="Payment Method"
                onChange={(e) => setEditData({ ...editData, paymentMethod: e.target.value })}
              >
                <MenuItem value="cash">Cash</MenuItem>
                <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
                <MenuItem value="cheque">Cheque</MenuItem>
                <MenuItem value="upi">UPI</MenuItem>
                <MenuItem value="card">Card</MenuItem>
              </Select>
            </FormControl>

            {editData.paymentMethod === "bank_transfer" && (
              <FormControl fullWidth>
                <InputLabel>Bank</InputLabel>
                <Select
                  value={editData.bank}
                  label="Bank"
                  onChange={(e) => setEditData({ ...editData, bank: e.target.value })}
                >
                  {mockBanks.map((bank) => (
                    <MenuItem key={bank} value={bank}>
                      {bank}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )} */}

            {/* <TextField
              fullWidth
              label="Transaction Reference"
              value={editData.transactionReference}
              onChange={(e) => setEditData({ ...editData, transactionReference: e.target.value })}
            />

            <TextField
              fullWidth
              label="Notes"
              multiline
              rows={2}
              value={editData.notes}
              onChange={(e) => setEditData({ ...editData, notes: e.target.value })}
            /> */}

            <TextField
              fullWidth
              label="Edit Comment (Required)"
              multiline
              rows={3}
              value={editData.editComment}
              onChange={(e) => setEditData({ ...editData, editComment: e.target.value })}
              error={!editData.editComment.trim() && editError !== ""}
              helperText="Please explain the reason for editing this receipt"
              required
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleEditSubmit}
            variant="contained"
            disabled={updateReceiptMutation.isLoading}
          >
            {updateReceiptMutation.isLoading ? "Saving..." : "Save Changes"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Print Dialog */}
      <Dialog
        open={printDialogOpen}
        onClose={() => {
          setPrintDialogOpen(false);
          setSelectedReceipt(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogContent>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Typography variant="h6">Receipt Preview</Typography>
            <Button variant="contained" startIcon={<PrintIcon />} onClick={handlePrint}>
              Print
            </Button>
          </Box>
          {selectedReceipt && (
            <PrintableReceipt ref={receiptRef} receipt={selectedReceipt} client={selectedReceipt.clientId} />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default AdminAllReceiptsPage;